import { meshtasticShown } from "./mapScopes";
import { agoText, isoDateTimeText, type Theme } from "./meshmapperCoverage";
import type { HexCell } from "./hexCoverage";

// Wording, colours and shapes for the FreqMapper coverage layer (issue #36). Pure; covered by tests/freqmapperCoverage.test.mjs.
// The data comes from GET /api/freqmapper/coverage (the answer the server built from the stored events); nothing here calls
// FreqMapper. The server already worked the events into hexes on the same 300 m grid the map draws (hexCoverage.ts), so the
// browser only turns each row into a cell.
//
// There is no "failed" or "not heard" class on purpose: the feed only holds evidence that something was heard, and a place
// with no row is simply a place with no evidence.

export const FM_LAYER_TITLE = "FreqMapper coverage";
export const FM_SETTINGS_PATH_TEXT = "Settings, FreqMapper";
export const FM_ZOOM_IN_MESSAGE = "Zoom in to see coverage";
export const FM_MESHCORE_ONLY_MESSAGE = "FreqMapper is Meshtastic only";
/** The legend's link to where the details are: the map only paints coverage. Opens in a new tab. */
export const FM_SOURCE_LINK = { text: "See details on FreqMapper", href: "https://freqmapper.net" } as const;
export const FM_EVIDENCE_LINE = "Shows only where FreqMapper has evidence. Blank does not mean no coverage.";

/** The hex classes, highest first. A hex takes the highest class present. */
export type FmClass = "tx" | "strong" | "fair" | "weak";

export interface FmClassInfo {
  cls: FmClass;
  /** Short name. */
  name: string;
  /** The legend wording. */
  text: string;
  /** Fill colour: FreqMapper's own Standard palette (twf.freqmapper.net). */
  fill: string;
  /** Outline colour: a darker shade of the fill (the palette gives one only for weak). */
  outline: string;
}

export const FM_CLASSES: readonly FmClassInfo[] = [
  { cls: "tx", name: "Verified TX", text: "Verified TX: heard by a FreqMapper Watcher", fill: "#16a34a", outline: "#15803d" },
  { cls: "strong", name: "RX strong", text: "Received here, strong", fill: "#6d28d9", outline: "#4c1d95" },
  { cls: "fair", name: "RX fair", text: "Received here, fair", fill: "#a78bfa", outline: "#7c3aed" },
  { cls: "weak", name: "RX weak", text: "Received here, weak", fill: "#ddd6fe", outline: "#a78bfa" },
];

export function classInfo(cls: string): FmClassInfo | null {
  return FM_CLASSES.find((c) => c.cls === cls) ?? null;
}

/** Fill opacity: the same as the MeshMapper layer (0.4 on the light map, 0.45 on the dark one). */
export { fillOpacity } from "./meshmapperCoverage";
export type { Theme };

/** A map-style expression that picks a colour from the feature's `type` (its class). */
export function fmColourExpression(which: "fill" | "outline"): unknown[] {
  const expr: unknown[] = ["match", ["get", "type"]];
  for (const c of FM_CLASSES) expr.push(c.cls, c[which]);
  expr.push(FM_CLASSES[FM_CLASSES.length - 1][which]);
  return expr;
}

// --- the stored answer -----------------------------------------------------------------------------------------------

export interface FmMeta {
  enabled: boolean;
  has_data: boolean;
  message?: string;
  data_version?: string;
  /** Unix seconds of the last successful ask of FreqMapper. */
  generated_at?: number | null;
  /** ok or failed (the latest refresh failed). */
  refresh_status?: string;
  importing?: boolean;
  hex_count?: number;
}

/** The painted coverage: one row per hex, [q, r, class] (see `fields`), on the 300 m hex grid at ref_lat. */
export interface FmFull extends FmMeta {
  hex_size_m: number;
  ref_lat: number;
  fields: string[];
  hexes: unknown[][];
}

export const FM_DEFAULT_FIELDS = ["q", "r", "cls"];

/** One hex: where it is and its class (as `type`, which the outline merging uses). */
export interface FmCell extends HexCell {
  type: FmClass;
}

/** Rows of [q, r, class] into cells. A row with an unknown class or no position is dropped: nothing is guessed. */
export function cellsFromRows(rows: readonly (readonly unknown[])[], fields?: readonly string[] | null): FmCell[] {
  const list = fields && fields.length ? fields : FM_DEFAULT_FIELDS;
  const qi = list.indexOf("q");
  const ri = list.indexOf("r");
  const ci = list.indexOf("cls");
  const out: FmCell[] = [];
  for (const row of rows) {
    const q = row[qi];
    const r = row[ri];
    const cls = row[ci];
    if (typeof q === "number" && typeof r === "number" && typeof cls === "string" && classInfo(cls)) out.push({ q, r, type: cls as FmClass });
  }
  return out;
}

// --- gating ----------------------------------------------------------------------------------------------------------

/** Whether coverage hexes are drawn at all: the layer is on in Settings, this viewer has it on, there are hexes, and
 * Meshtastic is visible (the protocol filter is not MeshCore only). */
export function fmDrawn(o: { available: boolean; layerOn: boolean; hasHexes: boolean; protocolFilter: string }): boolean {
  return o.available && o.layerOn && o.hasHexes && meshtasticShown(o.protocolFilter);
}

// --- the legend footer -------------------------------------------------------------------------------------------------

/** The legend's last line. After a failed refresh it says the data is old. */
export function footerLine(meta: { generated_at?: number | null; refresh_status?: string }, nowMs: number): string {
  const ago = agoText(meta.generated_at, nowMs);
  if (!ago) return "FreqMapper";
  if (meta.refresh_status === "failed") return `FreqMapper, last updated ${ago}, latest refresh failed`;
  return `FreqMapper, updated ${ago}`;
}

export const FM_IMPORTING_LINE = "Importing FreqMapper history. More coverage will appear as it arrives.";

// --- Settings block ------------------------------------------------------------------------------------------------------------

export const FM_SETTINGS_INTRO =
  "Uses FreqMapper's Partner coverage API. The key is shared with other systems, so this app keeps well under the shared request limit: at most 20 requests a minute, and it waits for the limit to reset when the shared budget runs low.";
export const FM_REFRESH_MIN = 1;
export const FM_REFRESH_MAX = 60;

/** What GET /api/freqmapper/status returns. */
export interface FreqMapperStatus {
  enabled: boolean;
  refresh_minutes: number;
  key_set: boolean;
  state: string;
  importing: boolean;
  last_attempt_at: string | null;
  last_fetch_at: string | null;
  last_result: string | null;
  last_error: string | null;
  tx_events: number;
  rx_events: number;
  hexes: number;
  requests_last_minute: number;
  request_cap: number;
  rate_limit: number | null;
  rate_remaining: number | null;
  next_poll_at: string | null;
}

/** Where the poll loop is, in a few words. */
export function stateText(state: string, importing = false): string {
  switch (state) {
    case "off":
      return "Switched off";
    case "no_key":
      return "No key set";
    case "blocked":
      return "Stopped: FreqMapper refused the request";
    case "backoff":
      return "Waiting before trying again";
    case "rate_wait":
      return importing ? "Importing history, pacing requests" : "Pacing requests to stay under the shared limit";
    case "waiting":
      return "Waiting for the next refresh";
    case "due":
      return importing ? "Importing history" : "Fetching now";
    default:
      return state;
  }
}

/** The result of the last request, in a few words. */
export function resultText(result: string | null | undefined): string {
  switch (result) {
    case "updated":
      return "New events stored";
    case "unchanged":
      return "Checked, nothing new";
    case "rate_wait":
      return "Asked to wait, will try again";
    case "rejected":
      return "Request refused";
    case "error":
      return "Failed";
    default:
      return "Not fetched yet";
  }
}

/** The refresh interval as typed: a whole number from 1 to 60, else null. */
export function parseRefreshMinutes(text: string): number | null {
  if (!/^\s*\d+\s*$/.test(text)) return null;
  const n = parseInt(text, 10);
  return n >= FM_REFRESH_MIN && n <= FM_REFRESH_MAX ? n : null;
}

export { isoDateTimeText };
